import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession
} from 'amazon-cognito-identity-js'
import { ref } from 'vue'
import { isSessionExpiredError } from '@/utils/sessionExpired'

let cypressRunning
try {
  if (Cypress) {
    cypressRunning = true
  }
} catch (e) {
  cypressRunning = false
}

const REFRESH_MARGIN_SECONDS = 10

let pendingSession = null
let sessionConfirmed = false

const secondsLeft = (session) => {
  const now = Math.floor(Date.now() / 1000) - session.getClockDrift()

  return Math.min(session.getIdToken().getExpiration(), session.getAccessToken().getExpiration()) - now
}

const readSession = (cognitoUser) => new Promise((resolve, reject) => {
  cognitoUser.getSession((err, session) => (err ? reject(err) : resolve(session)))
})

const refreshSession = (cognitoUser, refreshToken) => new Promise((resolve, reject) => {
  cognitoUser.refreshSession(refreshToken, (err, session) => (err ? reject(err) : resolve(session)))
})

const loadValidSession = async (cognitoUser, rejectedIdToken) => {
  try {
    const session = await readSession(cognitoUser)

    if (secondsLeft(session) > REFRESH_MARGIN_SECONDS && session.getIdToken().getJwtToken() !== rejectedIdToken) {
      return session
    }

    if (!session.getRefreshToken().getToken()) {
      throw new Error('Cannot retrieve a new session. Please authenticate.')
    }

    return await refreshSession(cognitoUser, session.getRefreshToken())
  } catch (error) {
    throw isSessionExpiredError(error) ? Object.assign(error, { isSessionExpired: true }) : error
  }
}

export default class CognitoAuth {
  constructor () {
    this.userSession = null
    if (cypressRunning) {
      this.configure({
        UserPoolId: Cypress.env('VUE_APP_COGNITO_USER_POOL_ID'),
        ClientId: Cypress.env('VUE_APP_COGNITO_USER_POOL_CLIENT_ID')
      })
    } else {
      this.configure({
        UserPoolId: process.env.VUE_APP_COGNITO_USER_POOL_ID,
        ClientId: process.env.VUE_APP_COGNITO_USER_POOL_CLIENT_ID
      })
    }
  }

  configure (options) {
    this.userPool = new CognitoUserPool({
      UserPoolId: options.UserPoolId,
      ClientId: options.ClientId
    })
  }

  getValidSession ({ rejectedIdToken } = {}) {
    const pending = pendingSession

    if (pending && (!rejectedIdToken || pending.rejectedIdToken === rejectedIdToken)) {
      return pending.promise
    }

    if (pending) {
      return pending.promise.catch(() => {}).then(() => this.getValidSession({ rejectedIdToken }))
    }

    const cognitoUser = this.getCurrentUser()
    if (!cognitoUser) {
      return Promise.resolve(null)
    }

    const promise = loadValidSession(cognitoUser, rejectedIdToken).then((session) => {
      sessionConfirmed = true

      return session
    }).finally(() => {
      pendingSession = null
    })
    pendingSession = { rejectedIdToken, promise }

    return promise
  }

  hadValidSession () {
    return sessionConfirmed
  }

  async isAuthenticatedPromise () {
    try {
      return (await this.getValidSession()) || false
    } catch (error) {
      if (error.isSessionExpired) {
        return false
      }

      throw error
    }
  }

  changePassword (oldPassword, newPassword, cb) {
    const cognitoUser = this.getCurrentUser()

    // getSession() will load valid tokens cached in the local store so we can
    // authenticate and change the users password
    cognitoUser.getSession((err) => {
      if (err) {
        return cb(err, false)
      }
      cognitoUser.changePassword(oldPassword, newPassword, (changeErr, result) => {
        if (changeErr) {
          return cb(changeErr)
        }

        // Invalidate all sessions after successful password change for security
        cognitoUser.globalSignOut({
          onSuccess: () => {
            cb(null, result)
          },
          onFailure: (globalSignOutErr) => {
            console.error('Global sign out failed after password change:', globalSignOutErr)
            // Still report success since password was changed successfully
            cb(null, result)
          }
        })
      })
    })
  }

  // Updates user information
  async updateAttributes (firstname, lastname, phoneNumber, company) {
    const cognitoUser = this.getCurrentUser()

    const attributeList = [
      new CognitoUserAttribute({
        Name: 'given_name',
        Value: firstname
      }),
      new CognitoUserAttribute({
        Name: 'family_name',
        Value: lastname
      }),
      new CognitoUserAttribute({
        Name: 'phone_number',
        Value: phoneNumber
      }),
      new CognitoUserAttribute({
        Name: 'custom:organisation',
        Value: company
      })
      // ... other attributes if needed
    ]

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err) => {
        if (err) {
          reject(err)
        } else {
          cognitoUser.updateAttributes(attributeList, (error, result) => {
            if (error) {
              reject(error)
            } else {
              resolve(result)
            }
          })
        }
      })
    })
  }

  /**
   * Set a signed-in session from JWT tokens (used when authenticating via Auth Proxy API).
   * This avoids a direct Cognito network call for login from the browser.
   */
  setSessionFromTokens (username, tokens) {
    const email = (username || '').toLowerCase()
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: this.userPool
    })

    const session = new CognitoUserSession({
      IdToken: new CognitoIdToken({ IdToken: tokens?.idToken }),
      AccessToken: new CognitoAccessToken({ AccessToken: tokens?.accessToken }),
      RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens?.refreshToken || '' })
    })

    cognitoUser.setSignInUserSession(session)
  }

  /**
   * Logout of your cognito session.
   */
  logout () {
    if (this.getCurrentUser()) {
      this.getCurrentUser().signOut((err) => {
        console.log('revoke token error', err)
      })
    }
  }

  /**
   * Global sign out - invalidates all tokens and sessions across all devices
   */
  globalSignOut () {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCurrentUser()
      if (!cognitoUser) {
        return reject(new Error('No current user'))
      }

      cognitoUser.getSession((err) => {
        if (err) {
          return reject(err)
        }

        cognitoUser.globalSignOut({
          onSuccess: (result) => {
            resolve(result)
          },
          onFailure: (error) => {
            reject(error)
          }
        })
      })
    })
  }

  async getIdTokenPromise ({ rejectedIdToken } = {}) {
    const session = await this.getValidSession({ rejectedIdToken })

    return session ? session.getIdToken().getJwtToken() : null
  }

  // The id token with the moment it stops being valid by this device's clock: the token's expiry
  // shifted by the session's clock drift, the same correction session.isValid() applies.
  async getIdTokenWithExpiryPromise () {
    const session = await this.getValidSession()

    if (!session) {
      return null
    }

    const idToken = session.getIdToken()

    return {
      token: idToken.getJwtToken(),
      expiresAt: (idToken.getExpiration() + session.getClockDrift()) * 1000,
      userId: idToken.payload['cognito:username']
    }
  }

  getCurrentUser () {
    return this.userPool?.getCurrentUser()
  }

  async getUserAttributes () {
    const user = this.getCurrentUser()
    return await new Promise((resolve, reject) => {
      user.getSession((err) => {
        if (err) {
          reject(err)
        } else {
          user.getUserAttributes((err, result) => {
            if (err) {
              reject(err)
            } else {
              resolve(result)
            }
          })
        }
      })
    })
  }

  async getSession () {
    const session = await this.getValidSession()

    if (!session) {
      throw new Error('No current user')
    }

    return session
  }

  // very primitive change listener
  onChange = ref(() => {})
}

export const CognitoAuthPlugin = function (app) {
  let cypressRunning
  try {
    if (Cypress) {
      cypressRunning = true
    }
  } catch (e) {
    cypressRunning = false
  }

  const cognitoAuth = new CognitoAuth()
  if (cypressRunning) {
    cognitoAuth.configure({
      UserPoolId: Cypress.env('VUE_APP_COGNITO_USER_POOL_ID'),
      ClientId: Cypress.env('VUE_APP_COGNITO_USER_POOL_CLIENT_ID')
    })
  } else {
    cognitoAuth.configure({
      UserPoolId: process.env.VUE_APP_COGNITO_USER_POOL_ID,
      ClientId: process.env.VUE_APP_COGNITO_USER_POOL_CLIENT_ID
    })
  }

  // Expose as a global property
  app.config.globalProperties.$cognitoAuth = cognitoAuth
}
